import { ElementRef, forwardRef, ViewChild } from '@angular/core';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import { ControlContainer, NgForm, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDate, NgbDateAdapter, NgbDateParserFormatter, NgbDatepicker, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { AppService } from '../../services/app.service';
import { Utils } from '../../core/Utils';

let counter = 0;

@Component({
    selector: 'app-date',
    templateUrl: './date.component.html',
    styleUrls: ['./date.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DateComponent),
            multi: true,
        }
    ]
})
export class DateComponent implements ControlValueAccessor, Validator  {
    constructor(
        private app: AppService,
        private adapter: NgbDateAdapter<Date>,
        private parser: NgbDateParserFormatter
    ) {
        this.id = `date-${counter++}`;
        this.placeholder = this.app.translate('dateFormatHint');
    }

    @Input() placeholder: string;
    @Input() required: boolean;
    @Input() disabled: boolean;
    @Input() min: Date | string;
    @Input() max: Date | string;
    @Input() marked: Date[] = [];

    @Output() dateChange = new EventEmitter<Date>();

    readonly id: string;

    get ngbMinDate(): NgbDateStruct {
        return this.dateToNgb(this.min);
    }

    get ngbMaxDate(): NgbDateStruct {
        return this.dateToNgb(this.max);
    }

    get value(): Date {
        return this._value;
    }

    @ViewChild('input', { static: true }) private input: ElementRef;
    @ViewChild('picker', { static: true }) private picker: NgbDatepicker;

    private _onChange = (obj: Date) => { };
    private _onTouched: any = () => { };
    private _value: Date;

    //ControlValueAccessor
    writeValue(obj: Date): void {
        this._value = obj;

        if (obj) {
            const ngb = this.dateToNgb(obj);

            this._value = obj;
            this.input.nativeElement.value = this.parser.format(ngb);
            this.picker.startDate = ngb;
        } else {
            this.input.nativeElement.value = '';
            this.picker.startDate = this.dateToNgb(new Date());
        }
    }

    registerOnChange(fn: (obj: Date) => void): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
    //ControlValueAccessor

    onChange(event) {
        const val = this.parser.parse(event.target.value);
        
        this._value = val ? this.adapter.toModel(val) : null;
        this.picker.startDate = val;

        this.emitChange();
    }

    onSelect(event: NgbDate) {
        this._value = this.adapter.toModel(event);
        this.picker.startDate = event;
        this.emitChange();
    }

    onBlur(event) {
        this._onTouched();
    }

    isMarked(date: NgbDate): boolean {
        const d = new Date(date.year, date.month - 1, date.day).getTime();
        return this.marked.some(t => t.getTime() == d);
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (this.value && (this.ngbMinDate || this.ngbMaxDate)) {
            const dt = Utils.ensureDate(this.value);
            const val = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 0, 0, 0);

            if (this.ngbMinDate) {
                const min = new Date(this.ngbMinDate.year, this.ngbMinDate.month - 1, this.ngbMinDate.day, 0, 0, 0);
                if (val < min) return { min: true };
            }

            if (this.ngbMaxDate) {
                const max = new Date(this.ngbMaxDate.year, this.ngbMaxDate.month - 1, this.ngbMaxDate.day, 0, 0, 0);
                if (val > max) return { max: true };
            }
        }

        return null;
    }

    private dateToNgb(date: Date | string): NgbDateStruct {
        if (!date) return undefined;

        if (typeof date === 'string') {
            date = new Date(date);
        }

        return {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate()
        };
    }

    private emitChange() {
        this._onChange(this._value);

        this.dateChange.emit(this._value);
    }
}
